import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone, Mail, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import '../../styles/wizard-steps.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

interface OTPStepProps {
  phoneNumber?: string;
  email?: string;
  channel: 'SMS' | 'EMAIL';
  onNext: (otp: string, verified: boolean) => void;
  onBack: () => void;
  tenantId?: string;
  formId?: string;
  token?: string;
}

interface OTPResponse {
  message: string;
  validitySeconds: number;
  cooldownSeconds: number;
  resendsRemaining: number;
  dispatched: boolean;
  retryAfterSeconds: number;
}

interface VerifyResponse {
  verified: boolean;
  message: string;
}

const OTPStep: React.FC<OTPStepProps> = ({
  phoneNumber,
  email,
  channel,
  onNext,
  onBack,
  tenantId,
  formId,
  token
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [resendsRemaining, setResendsRemaining] = useState(5);
  const [otpSent, setOtpSent] = useState(false);

  const identifier = channel === 'SMS' ? phoneNumber : email;
  const maskedIdentifier = channel === 'SMS' 
    ? `******${phoneNumber?.slice(-4) || '****'}`
    : email ? `${email.charAt(0)}***@${email.split('@')[1] || '***'}` : '***';

  // Build headers with optional token for authentication
  const getHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['X-Form-Token'] = token;
    }
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
    return headers;
  };

  const sendOTP = useCallback(async () => {
    if (!identifier) {
      setError('No contact information available');
      return;
    }

    setIsSending(true);
    setError('');
    setSuccessMessage('');

    try {
      const endpoint = channel === 'SMS' 
        ? `${API_BASE_URL}/consent/v1/otp/send/sms`
        : `${API_BASE_URL}/consent/v1/otp/send/email`;

      const payload = channel === 'SMS' 
        ? { phoneNumber: identifier, formId }
        : { email: identifier, formId };

      const response = await axios.post<{ data: OTPResponse }>(endpoint, payload, {
        headers: getHeaders()
      });
      const result = response.data.data;

      if (result.dispatched) {
        setOtpSent(true);
        setSuccessMessage(`OTP sent to ${maskedIdentifier}`);
        setResendTimer(result.cooldownSeconds);
        setResendsRemaining(result.resendsRemaining);
      } else {
        setError(result.message || 'Failed to send OTP');
        if (result.retryAfterSeconds > 0) {
          setResendTimer(result.retryAfterSeconds);
        }
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to send OTP';
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, channel, maskedIdentifier, formId, token, tenantId]);

  // Send OTP on mount
  useEffect(() => {
    if (!otpSent && identifier) {
      sendOTP();
    }
  }, [otpSent, identifier, sendOTP]);

  // Countdown timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter complete 6-digit OTP');
      return;
    }

    if (!identifier) {
      setError('No identifier available for verification');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await axios.post<{ data: VerifyResponse }>(
        `${API_BASE_URL}/consent/v1/otp/verify`,
        { identifier, otp: otpValue },
        { headers: getHeaders() }
      );

      const result = response.data.data;

      if (result.verified) {
        onNext(otpValue, true);
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Verification failed. Please try again.';
      setError(errorMsg);
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || resendsRemaining <= 0) return;
    setOtp(['', '', '', '', '', '']);
    await sendOTP();
    document.getElementById('otp-0')?.focus();
  };

  const isComplete = otp.every(digit => digit !== '');
  const canResend = resendTimer === 0 && resendsRemaining > 0;

  return (
    <div className="wizard-step otp-step">
      <div className="step-header">
        <div className="step-icon">{channel === 'SMS' ? <Smartphone size={24} /> : <Mail size={24} />}</div>
        <h2 className="step-title">Verify Your Identity</h2>
        <p className="step-subtitle">{channel === 'SMS' ? 'SMS' : 'Email'} OTP Verification</p>
      </div>

      <div className="otp-info">
        <p>We've sent a 6-digit verification code to:</p>
        <p className="phone-number">{maskedIdentifier}</p>
        <p className="helper-text">Please enter the code below to continue</p>
      </div>

      {successMessage && (
        <div className="success-message" style={{ color: '#22c55e', marginBottom: '16px' }}>
          <CheckCircle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> {successMessage}
        </div>
      )}

      <div className="otp-input-container" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="otp-input"
            autoFocus={index === 0}
            disabled={isVerifying}
          />
        ))}
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> {error}
        </div>
      )}

      <div className="resend-section">
        {canResend ? (
          <button 
            onClick={handleResend} 
            className="btn-link"
            disabled={isSending}
          >
            {isSending ? 'Sending...' : `Didn't receive the code? Resend OTP (${resendsRemaining} left)`}
          </button>
        ) : resendsRemaining > 0 ? (
          <p className="timer-text">
            Resend OTP in {resendTimer} seconds
          </p>
        ) : (
          <p className="timer-text" style={{ color: '#ef4444' }}>
            Maximum resends reached. Please try again later.
          </p>
        )}
      </div>

      <div className="step-actions">
        <button className="btn-link" onClick={onBack} disabled={isVerifying}>
          ← Back
        </button>
        <button
          onClick={handleVerify}
          disabled={!isComplete || isVerifying || !otpSent}
          className="btn btn-primary"
        >
          {isVerifying ? 'Verifying...' : 'Verify OTP →'}
        </button>
      </div>
    </div>
  );
};

export default OTPStep;
